import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// INTERFACES
import { environment } from '@app/env-service';
import { PaginationInterface } from '@app/interfaces/pagination.model';

// SERVICES
import { PricingTierInterface } from './../../modules/pricing/interfaces/pricing.interface';

@Injectable({
  providedIn: 'root',
})
export class PricingTierService {
  private readonly _deleting = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _pagination = new BehaviorSubject<PaginationInterface>(
    <PaginationInterface>{},
  );
  private readonly _pricingTiers = new BehaviorSubject<PricingTierInterface[]>(
    <PricingTierInterface[]>[],
  );
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);
  private pricingTiers :PricingTierInterface[] = [];

  deleting$ = this._deleting.asObservable();
  loading$ = this._loading.asObservable();
  pagination$ = this._pagination.asObservable();
  pricingTiers$ = this._pricingTiers.asObservable();
  submittingForm$ = this._submittingForm.asObservable();

  constructor(private http :HttpClient) {}

  get(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${environment.urlV2}/pricing/price-schedule-tiers`, { params })
      .subscribe(
        (response :any) => {
          this._loading.next(false);

          this.pricingTiers = response.data;

          this._pricingTiers.next(response.data);
          this._pagination.next(response.meta);
        },
        (response :any) => {
          this._loading.next(false);
          throwError(response.error);
        },
      );
  }

  create(pricingTier :Partial<PricingTierInterface>) :Observable<any> {
    this._submittingForm.next(true);

    return this.http
      .post(`${environment.urlV2}/pricing/price-schedule-tiers`, pricingTier)
      .pipe(
        map((response :PricingTierInterface) => {
          this._pricingTiers.next([...this.pricingTiers, response]);
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  update(pricingTier :PricingTierInterface) :Observable<any> {
    this._submittingForm.next(true);

    return this.http
      .put(
        `${environment.urlV2}/pricing/price-schedule-tiers/${pricingTier.id}`,
        pricingTier,
      )
      .pipe(
        map((response :PricingTierInterface) => {
          const updatedPricingTiers = this.pricingTiers.filter(
            (tier) => tier.id !== response.id,
          );
          updatedPricingTiers.push(response);
          this._pricingTiers.next(updatedPricingTiers);
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  delete(id :number) :Observable<any> {
    this._deleting.next(true);

    return this.http
      .delete(`${environment.urlV2}/pricing/price-schedule-tiers/${id}`)
      .pipe(
        map((response :any) => {
          const updatedPricingTiers = this.pricingTiers.filter(
            (tier) => tier.id !== id,
          );
          this._pricingTiers.next(updatedPricingTiers);
          this._deleting.next(false);

          return response;
        }),
        catchError((response) => {
          this._deleting.next(false);

          return throwError(response.error);
        }),
      );
  }

  setSubmittingForm(status :boolean) :void {
    this._submittingForm.next(status);
  }
}
